/**
 * useScheduleDraftEditorData — ScheduleDraft 工作台衍生数据模型入口。
 *
 * 本 hook 把草稿详情和 UI 轨配置组合为 canvas、Timetable、Pattern 与选中项
 * ViewModel。它只做派生计算和 debug trace，不发起写操作，也不持有本地交互
 * 状态。
 *
 * 设计文档 → docs/20_workspace_ux/21_workspace_layout.md
 * 骨架关联 → lib/canvas/buildScheduleDraftCanvasModel.js (画布模型), lib/workspace/buildScheduleDraftWorkspaceModels.js (表格/Pattern 模型), context/ScheduleDraftEditorWorkspaceContext.jsx (组合层)
 */

import { useEffect, useMemo } from "react";
import { buildScheduleDraftCanvasModel } from "../lib/scheduleDraftCanvas";
import { buildDemandReferences } from "../lib/canvas/scheduleDraftCanvasHelpers";
import {
    buildScheduleTrackKey,
    getDefaultEditingTrack,
    getDraftFlights,
    getEditingTracks,
    getReferenceDisplayTracks,
    getTrackFlights,
    isScheduleTrackKey,
    parseTrackKey,
    toArray,
} from "../lib/trackUtils";
import {
    buildScheduleDraftPatternModel,
    buildScheduleDraftSourceTrackOptions,
    buildScheduleDraftTimetableModel,
    resolveScheduleDraftSourceTrackMeta,
} from "../lib/workspace/buildScheduleDraftWorkspaceModels";
import { debugScheduleDraftTimeline } from "../lib/timelineDebug";

const EMPTY_CANVAS_MODEL = {
    tracks: [],
    contextLanes: [],
    items: [],
    timeWindow: null,
    availabilityContextTimeWindow: null,
    emptyState: null,
    demandReferences: [],
    editableTrackCount: 0,
    referenceTrackCount: 0,
    contextLaneCount: 0,
    primaryReferenceSource: null,
    primaryReferenceTrack: null,
};

const EMPTY_PATTERN_MODEL = {
    title: "Pattern 观察区",
    description: "当前未构建分段观察模型。",
    segments: [],
    overview: {
        sourceTrackLabel: "当前计划",
        patternType: "weekly",
        flightCount: 0,
        aircraftCount: 0,
    },
};

const EMPTY_LIGHTWEIGHT_FLIGHT_SELECTION = {
    itemById: new Map(),
    itemIds: new Set(),
};

function resolveBaselineEditingTrack(draft, currentEditingTrackId) {
    const editingTracks = getEditingTracks(draft);
    if (!editingTracks.length) {
        return null;
    }

    if (currentEditingTrackId) {
        const currentTrack = editingTracks.find((track) => String(track.id) === String(currentEditingTrackId));
        if (currentTrack) {
            return currentTrack;
        }
    }

    return getDefaultEditingTrack(draft) || editingTracks[0];
}

function getVisibleScheduleReferenceIds(visibleReferenceTrackIds = [], referenceTrackKeys = []) {
    const keys = toArray(referenceTrackKeys);
    if (keys.length) {
        return keys.filter(isScheduleTrackKey).map((key) => parseTrackKey(key).id).filter(Boolean);
    }
    return toArray(visibleReferenceTrackIds).filter(Boolean);
}

function buildLightweightCanvasModel(draft, {
    showEditableTracks,
    showDemandReferences,
    visibleReferenceTrackIds,
    referenceTrackKeys,
    externalReferenceTracks,
    currentEditingTrackId,
} = {}) {
    if (!draft) {
        return EMPTY_CANVAS_MODEL;
    }

    const editingTracks = getEditingTracks(draft);
    const referenceTracks = getReferenceDisplayTracks(draft);
    const visibleScheduleReferenceIds = getVisibleScheduleReferenceIds(visibleReferenceTrackIds, referenceTrackKeys);
    const visibleScheduleReferenceIdSet = new Set(visibleScheduleReferenceIds.map(String));
    const baselineEditingTrack = resolveBaselineEditingTrack(draft, currentEditingTrackId);
    const comparedReferenceTrackId = baselineEditingTrack?.compared_reference_track_id;
    const primaryReferenceTrack = comparedReferenceTrackId
        ? referenceTracks.find((track) => String(track.id) === String(comparedReferenceTrackId)) || null
        : null;
    const primaryReferenceSource = primaryReferenceTrack
        ? {
              key: buildScheduleTrackKey(primaryReferenceTrack.id),
              label: primaryReferenceTrack.name || primaryReferenceTrack.id,
              track: primaryReferenceTrack,
          }
        : null;

    return {
        ...EMPTY_CANVAS_MODEL,
        demandReferences: buildDemandReferences(draft, showDemandReferences),
        editableTrackCount: showEditableTracks === false ? 0 : editingTracks.length,
        referenceTrackCount: visibleScheduleReferenceIdSet.size + toArray(externalReferenceTracks).length,
        primaryReferenceSource,
        primaryReferenceTrack,
    };
}

function buildDraftFlightItemId(draftId, flightId) {
    return `draft:${draftId}:${flightId}`;
}

function buildLightweightSelectedFlightItem(draft, track, flight, itemId) {
    return {
        id: itemId,
        trackId: track?.id ? `editing:${track.id}` : "editing:current",
        itemType: "flight",
        title: flight?.flight_number || "未命名航班",
        start: flight?.std || null,
        end: flight?.sta || flight?.std || null,
        variant: "editable",
        meta: {
            flight,
            scheduleTrack: track || null,
            trackKind: "editable",
            sourceLabel: track?.name || draft?.name || "当前计划",
            usesEffectiveTime: false,
        },
    };
}

function buildLightweightFlightSelectionModel(draft) {
    if (!draft?.id) {
        return EMPTY_LIGHTWEIGHT_FLIGHT_SELECTION;
    }

    const itemById = new Map();
    const itemIds = new Set();
    const registerFlight = (track, flight) => {
        if (!flight?.id) {
            return;
        }

        const itemId = buildDraftFlightItemId(draft.id, flight.id);
        itemIds.add(itemId);
        if (!itemById.has(itemId)) {
            itemById.set(itemId, buildLightweightSelectedFlightItem(draft, track, flight, itemId));
        }
    };

    const editingTracks = getEditingTracks(draft);
    if (editingTracks.length) {
        editingTracks.forEach((track) => {
            getTrackFlights(track).forEach((flight) => registerFlight(track, flight));
        });
    } else {
        getDraftFlights(draft).forEach((flight) => registerFlight(null, flight));
    }

    return { itemById, itemIds };
}

export function useScheduleDraftEditorData({
    draft,
    selectedItemId,
    showEditableTracks,
    showDemandReferences,
    visibleReferenceTrackIds,
    referenceTrackKeys,
    externalReferenceTracks,
    sourceTrack,
    timetableTab,
    patternType,
    analysisResult,
    pendingChangeSet,
    airportConstraintsMap,
    aircraftAvailabilityWindowsByRegistration,
    isAircraftAvailabilityValidationPending,
    currentEditingTrackId,
    timelineFocusWindow,
    localValidationIssues,
    canDeleteTrack,
    isAgentWorking,
    enableTimelineProjection = true,
    enablePatternProjection = true,
}) {
    const canvasModel = useMemo(
        () => {
            const canvasConfig = {
                showEditableTracks,
                showDemandReferences,
                visibleReferenceTrackIds,
                referenceTrackKeys,
                externalReferenceTracks,
                currentEditingTrackId,
                timeWindow: timelineFocusWindow?.timeWindow,
                selectedItemId,
                airportConstraintsMap,
                aircraftAvailabilityWindowsByRegistration,
                canDeleteTrack,
                isAgentWorking,
            };

            return enableTimelineProjection
                ? buildScheduleDraftCanvasModel(draft, canvasConfig)
                : buildLightweightCanvasModel(draft, canvasConfig);
        },
        [aircraftAvailabilityWindowsByRegistration, airportConstraintsMap, canDeleteTrack, currentEditingTrackId, draft, enableTimelineProjection, externalReferenceTracks, isAgentWorking, referenceTrackKeys, selectedItemId, showDemandReferences, showEditableTracks, timelineFocusWindow?.timeWindow, visibleReferenceTrackIds],
    );

    const lightweightFlightSelection = useMemo(
        () => (enableTimelineProjection ? EMPTY_LIGHTWEIGHT_FLIGHT_SELECTION : buildLightweightFlightSelectionModel(draft)),
        [draft, enableTimelineProjection],
    );

    const selectedItem = useMemo(
        () => {
            if (!selectedItemId) {
                return null;
            }
            if (enableTimelineProjection) {
                return canvasModel.items.find((item) => item.id === selectedItemId) || null;
            }
            return lightweightFlightSelection.itemById.get(selectedItemId) || null;
        },
        [canvasModel.items, enableTimelineProjection, lightweightFlightSelection, selectedItemId],
    );

    const sourceTrackOptions = useMemo(
        () => buildScheduleDraftSourceTrackOptions(draft, visibleReferenceTrackIds, externalReferenceTracks, referenceTrackKeys),
        [draft, externalReferenceTracks, referenceTrackKeys, visibleReferenceTrackIds],
    );

    const activeSourceTrack = useMemo(
        () => resolveScheduleDraftSourceTrackMeta(draft, sourceTrack, visibleReferenceTrackIds, externalReferenceTracks, referenceTrackKeys),
        [draft, externalReferenceTracks, referenceTrackKeys, sourceTrack, visibleReferenceTrackIds],
    );

    const timetableModel = useMemo(
        () => buildScheduleDraftTimetableModel({
                draft,
                visibleReferenceTrackIds,
                referenceTrackKeys,
                externalReferenceTracks,
                timetableTab,
                analysisResult,
                pendingChangeSet,
                airportConstraintsMap,
                aircraftAvailabilityWindowsByRegistration,
                isAircraftAvailabilityValidationPending,
                localValidationIssues,
            }),
        [aircraftAvailabilityWindowsByRegistration, airportConstraintsMap, analysisResult, draft, externalReferenceTracks, isAircraftAvailabilityValidationPending, localValidationIssues, pendingChangeSet, referenceTrackKeys, timetableTab, visibleReferenceTrackIds],
    );

    const patternModel = useMemo(
        () => {
            if (!enablePatternProjection) {
                return {
                    ...EMPTY_PATTERN_MODEL,
                    overview: {
                        ...EMPTY_PATTERN_MODEL.overview,
                        patternType,
                    },
                };
            }

            return buildScheduleDraftPatternModel({
                draft,
                visibleReferenceTrackIds,
                referenceTrackKeys,
                externalReferenceTracks,
                sourceTrack,
                patternType,
            });
        },
        [draft, enablePatternProjection, externalReferenceTracks, patternType, referenceTrackKeys, sourceTrack, visibleReferenceTrackIds],
    );

    useEffect(() => {
        debugScheduleDraftTimeline("editor-data", {
            draftId: draft?.id || null,
            trackCount: canvasModel?.tracks?.length || 0,
            itemCount: canvasModel?.items?.length || 0,
            primaryReferenceTrack: canvasModel?.primaryReferenceTrack ? canvasModel.primaryReferenceTrack.name || canvasModel.primaryReferenceTrack.id : null,
            timeWindow: canvasModel?.timeWindow,
            firstItems: (canvasModel?.items || []).slice(0, 3).map((item) => ({
                id: item.id,
                trackId: item.trackId,
                start: item.start,
                end: item.end,
                title: item.title,
            })),
        });
    }, [canvasModel, draft?.id]);

    return {
        canvasModel,
        selectedItem,
        selectableFlightItemIds: lightweightFlightSelection.itemIds,
        sourceTrackOptions,
        activeSourceTrack,
        timetableModel,
        patternModel,
    };
}

export default useScheduleDraftEditorData;
